import { camelCase } from 'lodash';
import { CmsFilter, CmsFilterOperator } from 'types/cms';
import { useEffect, useReducer } from 'react';
import {
	getElementById,
	getElementIdByNodeId,
	getElementsById,
} from 'client/components/common/BuildingBlocks/utils/common';
import { STORY_ROOT_ID } from 'client/constants/common';
import { analytics } from 'utils/analytics';
import { useClientSelector } from 'client/reducers';
import { selectCardId } from 'client/reducers/card/selectors';
import { selectStoryId } from 'client/reducers/story/selectors';
import { isVectorSearch } from 'common/utils/cms/vector-search';
import { FIELD_COMP_NAME_ATTR } from '../Fields/constants';
import css from './CmsRepeater.scss';

type CustomFilter = Extract<CmsFilter, { type: 'filter' }>;

type Condition = {
	field?: string;
	value: string;
	operator?: CmsFilterOperator;
	vectorMinimumScore?: number;
};

type FilterReducerState = {
	search: Condition | null;
	/** Interactive custom filter only (click triggers). */
	filter: Condition | null;
	/** Start-of-load default; omitted while search or custom filter is active. */
	defaultFilter: Condition | null;
};

export type FilterReducerAction =
	| { type: 'search'; payload: Condition | null }
	| { type: 'filter'; payload: Condition | null };

export function useFilterReducer(initialFilters: CmsFilter[]) {
	return useReducer(
		(state: FilterReducerState, action: FilterReducerAction) => {
			switch (action.type) {
				case 'search':
					return { ...state, search: action.payload };
				case 'filter':
					return { ...state, filter: action.payload };
				default:
					return state;
			}
		},
		initialFilters.reduce(
			(acc, filter) => {
				if (filter.type === 'default') {
					acc.defaultFilter = { field: filter.field, value: filter.value, operator: filter.operator };
				}
				return acc;
			},
			{ search: null, filter: null, defaultFilter: null } as FilterReducerState
		)
	);
}

export const SEARCH_FIELD_EVENT = 'change';

interface FilterTriggerListenersParams {
	filters: CmsFilter[];
	setFilters: (action: FilterReducerAction) => void;
}

// Add event listeners for filter field
export function useFilterTriggers({ filters, setFilters }: FilterTriggerListenersParams) {
	const cardId = useClientSelector(selectCardId)!;
	const storyId = useClientSelector(selectStoryId)!;

	useEffect(() => {
		const controller = new AbortController();
		const rootTarget = document.getElementById(STORY_ROOT_ID);
		const customFilters = filters.filter((filter): filter is CustomFilter => filter.type === 'filter');
		const filterByComponentId = new Map(customFilters.map(filter => [filter.componentId, filter]));

		filters.forEach(addEventListener);

		if (filterByComponentId.size > 0) {
			document.addEventListener(
				'click',
				event => {
					for (
						let target = event.target as HTMLElement | null;
						target && target !== rootTarget;
						target = target.parentElement
					) {
						const targetId = getElementIdByNodeId(target.id);
						const filter = targetId ? filterByComponentId.get(targetId) : undefined;
						// eslint-disable-next-line no-continue
						if (!filter) continue;

						// prevent target action, for example prevent Button to navigate
						event.stopPropagation();
						onFilterChange(target, filter);
						break;
					}
				},
				{ signal: controller.signal, capture: true }
			);
		}

		function addEventListener(filter: CmsFilter) {
			switch (filter.type) {
				case 'search': {
					const input = getElementById(filter.componentId)?.querySelector('input');
					input?.addEventListener(
						SEARCH_FIELD_EVENT,
						event => {
							const value = (event.target as HTMLInputElement).value.trim();
							if (!value) {
								setFilters({ type: 'search', payload: null });
							} else if (isVectorSearch(filter)) {
								setFilters({
									type: 'search',
									payload: {
										value,
										vectorMinimumScore: filter.vectorMinimumScore,
									},
								});
							} else {
								setFilters({
									type: 'search',
									payload: { field: filter.field, value, operator: filter.operator },
								});
							}

							const componentNameDataKey = camelCase(FIELD_COMP_NAME_ATTR.replace('data-', ''));
							const layerName =
								(event.target as HTMLInputElement).dataset[componentNameDataKey] ?? 'Unknown layer';

							analytics.onSearchQuery({ cardId, storyId, layerName, searchTerm: value });
							analytics.onSearchPerformed({ cardId, storyId, layerName });
						},
						{ signal: controller.signal }
					);
					break;
				}
				case 'filter': {
					getElementsById(filter.componentId).forEach(el => el.classList.add(css.filterTrigger));
					break;
				}
				case 'default':
					break;
				default:
					break;
			}
		}

		function onFilterChange(target: HTMLElement, filter: CustomFilter) {
			const isActive = target.classList.contains(css.active);
			const triggerEls = getElementsById(filter.componentId);

			if (isActive) {
				triggerEls.forEach(el => el.classList.remove(css.active));
			} else {
				resetActiveFilter();
				triggerEls.forEach(el => el.classList.add(css.active));
			}

			const payload = isActive ? null : { field: filter.field, value: filter.value, operator: filter.operator };
			setFilters({ type: 'filter', payload });
		}

		function resetActiveFilter() {
			customFilters.forEach(filter => {
				getElementsById(filter.componentId).forEach(el => el.classList.remove(css.active));
			});
		}

		return () => controller.abort();
	}, [storyId, cardId, filters, setFilters]);

	return null;
}
